import { useState } from '#imports'
import { computed } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useHttpStore } from '@backmarket/nuxt-module-http-v2/useHttpStore'
import { camelizeKeys } from '@backmarket/utils/object/camelizeKeys'
import { cloneDeep } from '@backmarket/utils/object/cloneDeep'
import type { Camelized } from 'humps'

import type { GetUserAuthResponse } from '../api/auth/types'

// this prevents other layers using this composable from having type issues
import type {} from '../api/auth/specs'

type UserStoreState = {
  firstName?: string
  lastName?: string
  email?: string
  emailHashed?: string
  isShadowingClient: boolean
  isShadowingMerchant: boolean
  clientId?: number
  merchantId?: number
  cartItemsCount: number
  lastOrderDate?: Date
  numberOfOrders: number
  phone?: {
    dial: string
    phone: string
  }
  lifetimeRevenue: {
    amount: number
    currency?: string
  }
  detectedCountryCode?: string
  permissions: string[]
  userId?: number
  bmUserId?: string
  // @deprecated do not use this directly
  isStaff: boolean
}

type PrefetchUserPromise = Promise<Camelized<GetUserAuthResponse>>

const defaultState: UserStoreState = {
  firstName: undefined,
  lastName: undefined,
  email: undefined,
  emailHashed: undefined,
  isShadowingClient: false,
  isShadowingMerchant: false,
  clientId: undefined,
  merchantId: undefined,
  cartItemsCount: 0,
  lastOrderDate: undefined,
  numberOfOrders: 0,
  phone: undefined,
  lifetimeRevenue: {
    amount: 0,
    currency: undefined,
  },
  detectedCountryCode: undefined,
  permissions: [],
  userId: undefined,
  bmUserId: undefined,
  // @deprecated do not use this directly
  isStaff: false,
}

/**
 * User store composable for managing authenticated user data.
 */
export const useUserStore = () => {
  const state = useState<UserStoreState>('USER_STATE', () =>
    cloneDeep(defaultState),
  )
  const httpStore = useHttpStore()
  const $httpFetch = useHttpFetch()

  /**
   * Prefetch user without filling the store.
   * This is useful to start fetching without interfering with the hydration process.
   */
  async function prefetchUser(): PrefetchUserPromise {
    return camelizeKeys(await $httpFetch('/bm/user/auth'))
  }

  /**
   * Fetch the currently authenticated user information.
   *
   * In order to retrieve all the user information from the Badoom endpoint, a
   * proper `sessionid` cookie must be passed alongside the request. If you are
   * already using the `useLogin` composable to handle the authentication flow,
   * this is transparent for you, and all you have to do is call this action.
   *
   *
   * @example
   * const userStore = useUserStore()
   *
   * userStore.firstName // undefined
   * userStore.lastName // undefined
   *
   * await userStore.fetchUser()
   *
   * userStore.firstName // string
   * userStore.lastName // string
   */
  async function fetchUser(prefetchPromise?: PrefetchUserPromise) {
    const user = prefetchPromise ? await prefetchPromise : await prefetchUser()

    state.value.firstName = user.firstName
    state.value.lastName = user.lastName
    state.value.email = user.username
    state.value.emailHashed = user.emailHashed
    state.value.isShadowingClient = user.isStaffShadowingClient
    state.value.isShadowingMerchant = user.isStaffShadowingMerchant
    state.value.clientId = user.clientId
    state.value.merchantId = user.merchantId
    state.value.cartItemsCount = user.itemsInCartCount || 0
    state.value.numberOfOrders = user.numberOfOrders || 0
    state.value.permissions = user.permissions

    // @deprecated do not use this directly
    state.value.isStaff = user.isStaff

    if (user.countryDialInCode && user.phone) {
      state.value.phone = {
        dial: user.countryDialInCode,
        phone: user.phone,
      }
    }

    if (user.lifetimeRevenue) {
      state.value.lifetimeRevenue = user.lifetimeRevenue
    }

    if (user.lastOrderDate) {
      state.value.lastOrderDate = new Date(user.lastOrderDate)
    }

    state.value.userId = user.id ?? undefined

    if (user.bmUserId) {
      state.value.bmUserId = user.bmUserId
    }

    if (user.detectedCountryCode) {
      state.value.detectedCountryCode = user.detectedCountryCode
    }

    httpStore.value.csrf = user.csrf
  }

  /**
   * Returns a boolean indicating whether we already tried to retrieve the
   * user information or not.
   *
   * Checking the `csrf` value is the best way to know that since it will
   * always be defined after the authentication request was fired.
   */
  const isUserFetched = computed(() => Boolean(httpStore.value.csrf))

  /**
   * Returns a boolean indicating whether the user is authenticated or not.
   * This is based on the presence of the `email` property.
   */
  const isUserAuthenticated = computed(() => Boolean(state.value.email))

  /**
   * Returns a boolean indicating whether the user is authenticated as a client
   * or not. This is based on the presence of the `clientId` property.
   */
  const isUserAuthenticatedClient = computed(() =>
    Boolean(state.value.clientId),
  )

  /**
   * Returns a boolean indicating whether the user is authenticated as a seller
   * or not. This is based on the presence of the `merchantId` property.
   */
  const isUserAuthenticatedSeller = computed(() =>
    Boolean(state.value.merchantId),
  )

  /**
   * Returns a boolean indicating whether the user is authenticated as a staff
   * or not. This is based on the presence of the `authz-group-staff` permission.
   */
  const isUserAuthenticatedStaff = computed(
    () =>
      state.value.permissions.includes('group-staff') ||
      // To be cleaned once the Okta group is fully migrated to contain all staff users
      state.value.isStaff,
  )

  function $reset() {
    state.value = cloneDeep(defaultState)
  }

  /**
   * Set email of the user
   */
  function setEmail(email: string) {
    state.value.email = email
  }

  return {
    user: state,
    // actions
    fetchUser,
    prefetchUser,
    // mutations
    setEmail,
    $reset,
    // getters
    isUserFetched,
    isUserAuthenticated,
    isUserAuthenticatedClient,
    isUserAuthenticatedSeller,
    isUserAuthenticatedStaff,
  }
}
